// frontend/src/pages/SkillsList.js - Supabase対応版
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getStaffList } from "../supabase";
import { useOrganization } from "../contexts/OrganizationContext";

function SkillsList() {
  const { currentOrganization } = useOrganization(); 
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [selectedYear, setSelectedYear] = useState("all");
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  // 年度を計算（4月始まり）
const now = dayjs();
const currentFiscalYear = now.month() >= 3 ? now.year() : now.year() - 1;

  useEffect(() => {
    const fetchStaff = async () => {
      if (!currentOrganization?.id) {  // ← 追加
        console.log("⚠️ 組織IDなし - スキップ");
        return;
      }
      
      try {
        setLoading(true);
        
        const organizationId = currentOrganization.id;  // ← 修正
        console.log("🔍 取得する組織ID:", organizationId);
        
        // Supabaseからスタッフデータを取得
        const data = await getStaffList(organizationId);  // ← 修正
        
        console.log(`✅ スタッフデータ取得完了: ${data.length}人`);
        
        // 年次を計算
        const staffWithGeneration = data.map(staff => ({
          ...staff,
          generation: currentFiscalYear - Number(staff.year || currentFiscalYear),
        }));
  
        // 学年のリスト生成（昇順）
        const uniqueYears = [...new Set(staffWithGeneration.map(d => d.generation))]
          .filter(year => !isNaN(year))
          .sort((a, b) => a - b);
  
        setStaffList(staffWithGeneration);
        setYears(uniqueYears);
        setFilteredStaff(staffWithGeneration);
      } catch (error) {
        console.error("スタッフデータ取得エラー:", error);
      } finally {
        setLoading(false);
      }
    };
  
    fetchStaff();
  }, [currentFiscalYear, currentOrganization?.id]);

  useEffect(() => {
    let filtered = [...staffList];

    if (selectedYear !== "all") {
      filtered = filtered.filter((s) => s.generation === Number(selectedYear));
    }

    if (searchKeyword.trim() !== "") {
      const lower = searchKeyword.toLowerCase();
      filtered = filtered.filter((s) =>
        `${s.last_name}${s.first_name}`.toLowerCase().includes(lower)
      );
    }

    // 入職年順にソート
    filtered.sort((a, b) => a.generation - b.generation);

    setFilteredStaff(filtered);
  }, [staffList, selectedYear, searchKeyword]);

  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-700">スタッフデータを読み込み中...</p>
          {currentOrganization?.name && (
            <p className="mt-2 text-sm text-gray-500">{currentOrganization.name}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
            <h1 className="text-2xl font-bold text-gray-800">経験録 - スタッフ一覧</h1>
            <button
              onClick={() => navigate('/skills/by-level')}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shadow-sm transition"
            >
              <span>📊</span>
              <span>レベル別スタッフ一覧</span>
            </button>
          </div>
          
          {/* フィルター・検索エリア */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            {/* 学年フィルター */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                学年フィルター
              </label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">全学年</option>
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year + 1}年目
                  </option>
                ))}
              </select>
            </div>

            {/* 名前検索 */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                名前検索
              </label>
              <input
                type="text"
                placeholder="名前で検索..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>

          {/* 結果表示 */}
          <div className="text-sm text-gray-600 mb-4">
            {filteredStaff.length}名のスタッフが見つかりました
            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-xs">
              聖マリアンナ医科大学病院
            </span>
          </div>
        </div>

        {/* スタッフカード一覧 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredStaff.map((staff) => (
            <div
              key={staff.id}
              className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 cursor-pointer border hover:border-teal-500"
              onClick={() => navigate(`/skills/chart/${staff.id}`)}
            >
              <div className="p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                  {staff.last_name} {staff.first_name}
                </h3>
                
                <div className="space-y-1 text-sm text-gray-600 mb-3">
                  <p>
                    <span className="font-medium">{staff.generation + 1}年目</span>
                    <span className="text-gray-500 ml-2">({staff.year}年入職)</span>
                  </p>
                  <p>
                    <span className="text-gray-500">社員番号:</span> {staff.employee_id}
                  </p>
                  {staff.role && (
                    <p>
                      <span className="text-gray-500">役職:</span> {staff.role}
                    </p>
                  )}
                </div>

                {/* アクションボタン */}
                <div className="pt-3 border-t border-gray-200">
                  <button 
                    className="w-full bg-teal-600 hover:bg-teal-700 text-white py-2 px-4 rounded-md text-sm font-medium transition-colors duration-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/skills/chart/${staff.id}`);
                    }}
                  >
                    詳細を見る
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 結果が0件の場合 */}
        {filteredStaff.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="text-gray-500 mb-4">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2M4 13h2m13-8V4a1 1 0 00-1-1H7a1 1 0 00-1 1v1m8 0V4.5" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              スタッフが見つかりませんでした
            </h3>
            <p className="text-gray-500">
              検索条件を変更してもう一度お試しください
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default SkillsList;